import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { BIN_LABELS, isRankedPost } from '../utils/rankingBins';

function localDateString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function MovieDetailModal({
  movie,
  existingPost,
  open,
  initialStep = 'detail',
  onClose,
  onBeginRanking,
  onOpenProfile,
  onAddToWatchMovieList,
  onRemoveFromWatchlist,
  viewerHasWatchedMovie = false,
  inWatchlist = false,
  watchlistEntryId,
}) {
  const { showToast } = useToast();
  const [step, setStep] = useState('detail');
  const [watchedAt, setWatchedAt] = useState(localDateString());
  const [bookmarkBusy, setBookmarkBusy] = useState(false);
  const [friendsActivity, setFriendsActivity] = useState([]);
  const [friendsActivityStatus, setFriendsActivityStatus] = useState('idle');
  const [friendsActivityError, setFriendsActivityError] = useState(null);
  const hasOverview = Boolean((movie?.overview || '').trim());
  const hasOriginalTitle = Boolean(
    (movie?.original_title || '').trim() &&
    (movie?.original_title || '').trim() !== (movie?.title || '').trim()
  );
  const hasMetadataRow = Boolean(
    movie?.original_language ||
    movie?.vote_average != null ||
    movie?.vote_count != null ||
    movie?.popularity != null
  );
  const genreNames = Array.isArray(movie?.genre_names) ? movie.genre_names : [];
  const genreIds = Array.isArray(movie?.genre_ids) ? movie.genre_ids : [];

  useEffect(() => {
    if (open && movie) {
      setStep(initialStep === 'pickBin' ? 'pickBin' : 'detail');
      setWatchedAt(localDateString());
    }
  }, [open, movie, initialStep]);

  useEffect(() => {
    let cancelled = false;
    async function loadFriendsActivity() {
      if (!open || !movie) {
        setFriendsActivity([]);
        setFriendsActivityStatus('idle');
        setFriendsActivityError(null);
        return;
      }
      const params = new URLSearchParams();
      if (movie.tmdb_id != null) {
        params.set('tmdbId', String(movie.tmdb_id));
      } else if (movie.title && movie.year != null) {
        params.set('title', String(movie.title));
        params.set('year', String(movie.year));
      } else {
        setFriendsActivity([]);
        setFriendsActivityStatus('idle');
        setFriendsActivityError(null);
        return;
      }
      setFriendsActivityStatus('loading');
      setFriendsActivityError(null);
      try {
        const res = await fetch(`/api/movies/friends-activity?${params.toString()}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.ok) {
          throw new Error(data.error || 'Failed to load friend activity');
        }
        setFriendsActivity(Array.isArray(data.results) ? data.results : []);
        setFriendsActivityStatus('success');
      } catch (e) {
        if (cancelled) return;
        setFriendsActivity([]);
        setFriendsActivityStatus('error');
        setFriendsActivityError((e && e.message) || 'Failed to load friend activity');
      }
    }
    loadFriendsActivity();
    return () => {
      cancelled = true;
    };
  }, [open, movie]);

  const toggleWatchlistBookmark = useCallback(async () => {
    if (viewerHasWatchedMovie || bookmarkBusy) return;
    setBookmarkBusy(true);
    try {
      if (inWatchlist) {
        if (watchlistEntryId && onRemoveFromWatchlist) {
          await onRemoveFromWatchlist(watchlistEntryId);
          showToast('Removed from watchlist');
        }
      } else if (movie && onAddToWatchMovieList) {
        await onAddToWatchMovieList(movie);
        showToast('Saved to watchlist');
      }
    } finally {
      setBookmarkBusy(false);
    }
  }, [
    bookmarkBusy,
    viewerHasWatchedMovie,
    inWatchlist,
    movie,
    onAddToWatchMovieList,
    onRemoveFromWatchlist,
    watchlistEntryId,
    showToast,
  ]);

  if (!open || !movie) return null;

  const ranked = existingPost && isRankedPost(existingPost);
  const visibleFriendsActivity = friendsActivity.slice(0, 3);
  const hiddenFriendsCount = Math.max(0, friendsActivity.length - visibleFriendsActivity.length);

  const submitTier = (rankingBin) => {
    const shouldRerank =
      Boolean(ranked) &&
      existingPost?.movieId != null;
    onBeginRanking({
      selectedMovie: movie,
      watchedAt,
      rankingBin,
      createdAt: new Date().toISOString(),
      ...(shouldRerank
        ? {
            isRerank: true,
            movieId: existingPost.movieId,
          }
        : {}),
    });
    onClose();
  };

  return (
    <div className="flique-modal-backdrop" onClick={onClose}>
      <div
        className="flique-modal card flique-card flique-movie-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h2 className="h5 mb-0">Movie details</h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-light flique-modal-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <div className="d-flex gap-3 mb-3">
            {movie.poster_url ? (
              <img
                src={movie.poster_url}
                alt=""
                className="flique-movie-detail-poster"
              />
            ) : (
              <div className="flique-movie-detail-poster flique-movie-detail-poster-empty">
                —
              </div>
            )}
            <div>
              <h3 className="h5 mb-1">{movie.title}</h3>
              <p className="small text-muted mb-1">
                {movie.year ? `${movie.year}` : ''}
                {movie.director ? ` · ${movie.director}` : ''}
              </p>
              {hasOriginalTitle ? (
                <p className="small text-muted mb-1">
                  Original title: {movie.original_title}
                </p>
              ) : null}
              {hasMetadataRow ? (
                <p className="small text-muted mb-0">
                  {movie.original_language
                    ? `Language: ${String(
                        movie.original_language
                      ).toUpperCase()}`
                    : ''}
                  {movie.vote_average != null
                    ? `${movie.original_language ? ' · ' : ''}Rating: ${Number(
                        movie.vote_average
                      ).toFixed(1)}`
                    : ''}
                  {movie.vote_count != null ? ` (${movie.vote_count} votes)` : ''}
                </p>
              ) : null}
            </div>
          </div>
          {hasOverview ? (
            <p className="small text-muted mb-3">{movie.overview}</p>
          ) : null}
          {genreNames.length > 0 ? (
            <div className="d-flex flex-wrap gap-2 mb-3" aria-label="Genres">
              {genreNames.map((name) => (
                <span key={name} className="flique-search-status-chip">
                  {name}
                </span>
              ))}
            </div>
          ) : genreIds.length > 0 ? (
            <div className="d-flex flex-wrap gap-2 mb-3" aria-label="Genres">
              {genreIds.map((id) => (
                <span key={id} className="flique-search-status-chip">
                  Genre {id}
                </span>
              ))}
            </div>
          ) : null}
          <section className="flique-movie-detail-friends-activity mb-3" aria-live="polite">
            <h4 className="flique-movie-detail-friends-activity-title">Friend Activity</h4>
            {friendsActivityStatus === 'error' ? (
              <p className="small text-muted mb-0">
                Couldn&apos;t load friend activity right now.
                {friendsActivityError ? ` ${friendsActivityError}` : ''}
              </p>
            ) : null}
            {friendsActivityStatus === 'success' &&
            visibleFriendsActivity.length === 0 ? (
              <p className="small text-muted mb-0">
                None of your friends have watched this yet.
              </p>
            ) : null}
            {visibleFriendsActivity.length > 0 ? (
              <p className="small text-muted mb-0 flique-movie-detail-friends-activity-inline">
                {visibleFriendsActivity.map((entry, idx) => (
                  <React.Fragment key={entry.friendId}>
                    {idx > 0 ? ', ' : ''}
                    {typeof onOpenProfile === 'function' ? (
                      <button
                        type="button"
                        className="flique-movie-detail-friend-link"
                        onClick={() => onOpenProfile(entry.friendId)}
                        aria-label={`Open @${entry.name || 'friend'} profile`}
                      >
                        {entry.name || 'Friend'}
                      </button>
                    ) : (
                      <span className="flique-movie-detail-friend-name">
                        {entry.name || 'Friend'}
                      </span>
                    )}{' '}
                    {entry.sentence || 'watched this recently'}
                  </React.Fragment>
                ))}
                {hiddenFriendsCount > 0 ? `, +${hiddenFriendsCount} more friends` : ''}
              </p>
            ) : null}
          </section>

          {step === 'detail' && (
            <>
              <div className="flique-movie-detail-bookmark-row">
                {viewerHasWatchedMovie ? (
                  <span
                    className="flique-feed-engage-watched-badge"
                    role="status"
                    aria-label="You watched this movie"
                  >
                    <span
                      className="flique-feed-engage-watched-icon"
                      aria-hidden="true"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </span>
                    <span className="flique-feed-engage-watched-text">Watched</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="flique-feed-engage-bookmark-btn"
                    onClick={toggleWatchlistBookmark}
                    disabled={
                      bookmarkBusy || (inWatchlist && !watchlistEntryId)
                    }
                    aria-pressed={inWatchlist}
                    aria-label={
                      inWatchlist ? 'Remove from watchlist' : 'Save to watchlist'
                    }
                  >
                    <span
                      className="flique-feed-engage-bookmark-icon"
                      aria-hidden="true"
                    >
                      {inWatchlist ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                        </svg>
                      )}
                    </span>
                  </button>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label small" htmlFor="flique-watched-at">
                  Watched on
                </label>
                <input
                  id="flique-watched-at"
                  type="date"
                  className="form-control"
                  value={watchedAt}
                  max={localDateString()}
                  onChange={(e) => setWatchedAt(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn-flique w-100"
                disabled={!watchedAt}
                onClick={() => setStep('pickBin')}
              >
                {ranked ? 'Re-rank it' : 'Rank it'}
              </button>
            </>
          )}

          {step === 'pickBin' && (
            <>
              <p className="small text-muted mb-3">
                Pick how you felt about it. You&apos;ll only compare against other
                movies you felt the same way about.
              </p>
              <div className="flique-impressions flique-impressions--two mb-3">
                <button
                  type="button"
                  className="btn btn-loved"
                  onClick={() => submitTier('liked')}
                >
                  {BIN_LABELS.liked}
                </button>
                <button
                  type="button"
                  className="btn btn-dislike"
                  onClick={() => submitTier('disliked')}
                >
                  {BIN_LABELS.disliked}
                </button>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => setStep('detail')}
              >
                Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
